import { Trans } from "@lingui/macro";
import { InvoiceEntity, useInvoiceDetailsQuery } from "@smp/graphql";
import {
  IUiSelectOption,
  IUiSelectProps,
  UiBox,
  UiGrid,
  UiSelect,
  UiSpinner,
  UiTypography,
} from "@smp/ui";
import { useEffect, useMemo, useState } from "react";

import { PageFailure } from "@/components/feedback/pages/page_failure";
import { Screen } from "@/components/layout/screen";
import { gqlClient } from "@/hooks/use_graphql";

import { FormLegal } from "./form_legal";
import { FormNatural } from "./form_natural";

export const InvoiceForm = ({
  isModal,
  onClose,
}: {
  isModal?: boolean;
  onClose?: () => void;
}) => {
  const [invoiceMode, setInvoiceMode] = useState<InvoiceEntity>(
    InvoiceEntity.NaturalPerson,
  );

  const {
    data: invoiceDetailsData,
    isLoading: isInvoiceDetailsDataLoading,
    isError: isInvoiceDetailsDataError,
  } = useInvoiceDetailsQuery(gqlClient);

  useEffect(() => {
    invoiceDetailsData &&
      setInvoiceMode(invoiceDetailsData?.invoice_details.invoice_entity);
  }, [invoiceDetailsData, invoiceDetailsData?.invoice_details.invoice_entity]);

  const isOrganizationMode = invoiceMode === InvoiceEntity.LegalEntity;

  const options: IUiSelectOption[] = useMemo(
    () => [
      {
        label: <Trans>Custom</Trans>,
        value: "natural_person",
      },
      {
        label: <Trans>Company</Trans>,
        value: "legal_entity",
      },
    ],
    [],
  );

  const handleChange: NonNullable<IUiSelectProps["onChange"]> = (event) => {
    setInvoiceMode(event.target.value as InvoiceEntity);
  };

  if (isInvoiceDetailsDataLoading)
    return <UiSpinner title={<Trans>Loading</Trans>} />;

  return (
    <Screen
      title={<Trans>Invoice details</Trans>}
      back={isModal ? onClose : "up"}
    >
      {isInvoiceDetailsDataError ? (
        <PageFailure
          errorMessage={<Trans>An unknown error has occurred.</Trans>}
        />
      ) : (
        <>
          <UiGrid container spacing={2}>
            <UiGrid item xs={isModal ? 6 : 12}>
              <UiBox mb={4}>
                <UiTypography>
                  <Trans>* required fields</Trans>
                </UiTypography>
              </UiBox>

              <UiSelect
                fullWidth
                gutterBottom
                style={{ marginBottom: 16 }}
                label={<Trans>Choose invoice type</Trans>}
                value={invoiceMode}
                options={options}
                onChange={handleChange}
              />
            </UiGrid>
          </UiGrid>
          {isOrganizationMode ? (
            <FormLegal
              isModal={isModal}
              onCancel={isModal ? onClose : undefined}
            />
          ) : (
            <FormNatural
              isModal={isModal}
              onCancel={isModal ? onClose : undefined}
            />
          )}
        </>
      )}
    </Screen>
  );
};
